import * as React from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { toast } from '@/components/core/v2/toaster';
import { useUser } from '@/hooks/v2/use-user';
import axios from '@/lib/v2/axios';

interface RentalRequirement {
  id: string;
  community_id: string;
  name: string;
  description: string | null;
  is_required: boolean;
  sort_order: number;
}

export function CommunityRentalsPage(): React.JSX.Element {
  const { community } = useUser();
  const queryClient = useQueryClient();

  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editName, setEditName] = React.useState('');
  const [editDescription, setEditDescription] = React.useState('');
  const [editRequired, setEditRequired] = React.useState(true);

  // Fetch rental requirements
  const { data: requirements, isLoading } = useQuery({
    queryKey: ['rental-requirements', community?.id],
    queryFn: async () => {
      const response = await axios.get(`/api/v2/communities/${community?.id}/rental-requirements`);
      return (response.data?.requirements || []) as RentalRequirement[];
    },
    enabled: !!community?.id,
  });

  // Add requirement form
  const {
    control: addControl,
    handleSubmit: handleAddSubmit,
    reset: addReset,
    formState: { errors: addErrors },
  } = useForm({
    defaultValues: {
      name: '',
      description: '',
      is_required: true,
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: { name: string; description: string; is_required: boolean }) => {
      return await axios.post(`/api/v2/communities/${community?.id}/rental-requirements`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental-requirements', community?.id] });
      toast.success('Requirement added');
      addReset();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message ?? 'Failed to add requirement');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: { id: string; name: string; description: string; is_required: boolean }) => {
      return await axios.patch(
        `/api/v2/communities/${community?.id}/rental-requirements/${payload.id}`,
        {
          name: payload.name,
          description: payload.description,
          is_required: payload.is_required,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental-requirements', community?.id] });
      toast.success('Requirement updated');
      setEditingId(null);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message ?? 'Failed to update requirement');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return await axios.delete(`/api/v2/communities/${community?.id}/rental-requirements/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental-requirements', community?.id] });
      toast.success('Requirement removed');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message ?? 'Failed to remove requirement');
    },
  });

  const startEditing = (req: RentalRequirement) => {
    setEditingId(req.id);
    setEditName(req.name);
    setEditDescription(req.description || '');
    setEditRequired(req.is_required);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditName('');
    setEditDescription('');
    setEditRequired(true);
  };

  const saveEdit = () => {
    if (!editingId) return;
    updateMutation.mutate({
      id: editingId,
      name: editName.trim(),
      description: editDescription.trim(),
      is_required: editRequired,
    });
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '900px' }}>
      <Stack spacing={4}>
        {/* Introduction */}
        <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            Rental Requirements
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Define documentation and requirements that must be collected for rental units in your community.
            These requirements will be associated with units marked as rentals.
          </Typography>
        </Paper>

        {/* Add New Requirement */}
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Add New Requirement</Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <Stack spacing={3}>
              <Controller
                control={addControl}
                name="name"
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!addErrors.name}
                    fullWidth
                    helperText={addErrors.name?.message}
                    label="Requirement Name"
                    placeholder="e.g., Lease Agreement, Proof of Insurance"
                    required
                  />
                )}
                rules={{ required: 'Name is required' }}
              />

              <Controller
                control={addControl}
                name="description"
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Description (Optional)"
                    multiline
                    placeholder="Additional details about this requirement"
                    rows={2}
                  />
                )}
              />

              <Controller
                control={addControl}
                name="is_required"
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label="Required (must be provided by tenant)"
                  />
                )}
              />

              <Box>
                <LoadingButton
                  endIcon={<AddIcon />}
                  loading={createMutation.isPending}
                  onClick={handleAddSubmit((data) => createMutation.mutate(data))}
                  variant="contained"
                >
                  Add Requirement
                </LoadingButton>
              </Box>
            </Stack>
          </Box>
        </Paper>

        {/* Existing Requirements */}
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{`Requirements (${requirements?.length || 0})`}</Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            {isLoading ? (
              <Typography color="text.secondary">Loading requirements...</Typography>
            ) : requirements && requirements.length > 0 ? (
              <Stack spacing={2}>
                {requirements.map((req) => (
                  <Paper
                    key={req.id}
                    elevation={0}
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1.5,
                      backgroundColor: editingId === req.id ? 'action.hover' : 'background.paper',
                    }}
                  >
                    {editingId === req.id ? (
                      <Stack spacing={2}>
                        <TextField
                          fullWidth
                          label="Name"
                          onChange={(e) => setEditName(e.target.value)}
                          value={editName}
                        />
                        <TextField
                          fullWidth
                          label="Description"
                          multiline
                          onChange={(e) => setEditDescription(e.target.value)}
                          rows={2}
                          value={editDescription}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={editRequired}
                              onChange={(e) => setEditRequired(e.target.checked)}
                            />
                          }
                          label="Required"
                        />
                        <Stack direction="row" spacing={1}>
                          <LoadingButton
                            loading={updateMutation.isPending}
                            onClick={saveEdit}
                            size="small"
                            startIcon={<SaveIcon />}
                            variant="contained"
                          >
                            Save
                          </LoadingButton>
                          <Button onClick={cancelEditing} size="small" variant="outlined">
                            Cancel
                          </Button>
                        </Stack>
                      </Stack>
                    ) : (
                      <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontWeight: 600, mb: 0.5 }} variant="body1">
                            {req.name}
                            {req.is_required && (
                              <Typography
                                component="span"
                                sx={{ ml: 1, color: 'error.main', fontSize: '0.75rem' }}
                              >
                                *Required
                              </Typography>
                            )}
                          </Typography>
                          {req.description && (
                            <Typography color="text.secondary" variant="body2">
                              {req.description}
                            </Typography>
                          )}
                        </Box>
                        <Stack direction="row" spacing={0.5}>
                          <IconButton onClick={() => startEditing(req)} size="small">
                            <EditOutlined fontSize="small" />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => {
                              if (window.confirm(`Delete "${req.name}"?`)) {
                                deleteMutation.mutate(req.id);
                              }
                            }}
                            size="small"
                          >
                            <DeleteOutlined fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Stack>
                    )}
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Typography color="text.secondary">
                No rental requirements defined yet. Add your first requirement above.
              </Typography>
            )}
          </Box>
        </Paper>
      </Stack>
    </Box>
  );
}
